import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';
import { Popover } from '@material-ui/core';
import { ensureRcEmojiData, getRcEmojiData } from 'app/utils/chat/chatUtils';

const Picker = lazy(() => import('@emoji-mart/react'));

const PICKER_WIDTH = 284;
const PICKER_HEIGHT = 435;
const VIEWPORT_MARGIN = 16;

const POPOVER_PAPER_PROPS = {
    style: {
        width: PICKER_WIDTH,
        height: PICKER_HEIGHT,
        maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
        maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
        overflow: 'hidden',
    },
};

const CATEGORY_MAP = {
    people: { id: 'people', name: 'Smileys & People' },
    nature: { id: 'nature', name: 'Animals & Nature' },
    food: { id: 'foods', name: 'Food & Drink' },
    activity: { id: 'activity', name: 'Activity' },
    travel: { id: 'places', name: 'Travel & Places' },
    objects: { id: 'objects', name: 'Objects' },
    symbols: { id: 'symbols', name: 'Symbols' },
    flags: { id: 'flags', name: 'Flags' },
};
const CATEGORY_ORDER = ['people', 'nature', 'food', 'activity', 'travel', 'objects', 'symbols', 'flags'];

const hexToNative = hex => {
    if (!hex) return '';
    return hex
        .split('-')
        .map(code => String.fromCodePoint(parseInt(code, 16)))
        .join('');
};

const titleCase = id =>
    id
        .split('_')
        .map(w => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
        .join(' ');

// emoji-mart mutates the data object (splices categories[].emojis) on mount,
// so we cannot cache and reuse across mounts. Rebuild on every call.
// Source data shape: { categories: { [rcCat]: [id, ...] }, emojis: { [id]: { s: [{u, t?}], a: aliases[] } } }
const buildRcSafeEmojiData = () => {
    const rcEmojiData = getRcEmojiData();
    if (!rcEmojiData) return null;
    const emojis = {};
    const aliases = {};
    const byCategory = {};
    const src = rcEmojiData.emojis || {};
    const srcCats = rcEmojiData.categories || {};
    Object.keys(src).forEach(id => {
        const rec = src[id];
        const skins = (rec.s || [])
            .map(sk => {
                const native = hexToNative(sk.u);
                if (!native) return null;
                return { unified: sk.u.toLowerCase(), native };
            })
            .filter(Boolean);
        if (skins.length === 0) return;
        const aliasList = (rec.a || []).filter(Boolean);
        emojis[id] = {
            id,
            name: titleCase(id),
            keywords: [id, ...aliasList],
            skins,
            version: 1,
        };
        aliasList.forEach(alias => {
            if (alias && !aliases[alias]) aliases[alias] = id;
        });
    });
    Object.keys(srcCats).forEach(rcCat => {
        const cat = CATEGORY_MAP[rcCat];
        if (!cat) return;
        byCategory[cat.id] = srcCats[rcCat].filter(id => emojis[id]);
    });
    // NB: omit `name` — emoji-mart treats any category with `name` as a custom category
    // and strips it on remount via `isCustom = !!c.name`, leaving only Frequently Used.
    // Names come from the i18n data bundled by emoji-mart (or the default English labels).
    const categories = CATEGORY_ORDER.map(key => {
        const cat = CATEGORY_MAP[key];
        return { id: cat.id, emojis: byCategory[cat.id] || [] };
    }).filter(c => c.emojis.length > 0);
    return { categories, emojis, aliases };
};

const buildCustomCategory = list => {
    if (!list || list.length === 0) return undefined;
    return [
        {
            id: 'custom',
            name: 'Custom',
            emojis: list.map(e => ({
                id: e.name,
                name: e.name,
                keywords: (e.aliases || []).concat([e.name]),
                skins: [{ src: `/chat/emoji-custom/${encodeURIComponent(e.name)}.${e.extension || 'png'}` }],
            })),
        },
    ];
};

const hexToRgbTriple = hex => {
    if (!hex) return '0, 0, 0';
    const h = String(hex).replace('#', '');
    const full = h.length === 3 ? h.split('').map(x => x + x).join('') : h;
    const n = parseInt(full, 16);
    if (Number.isNaN(n)) return '0, 0, 0';
    return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
};

const PickerFrame = styled.div`
    width: ${PICKER_WIDTH}px;
    height: ${PICKER_HEIGHT}px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    color: ${({ theme }) => theme.material.colors.text.secondary};
    em-emoji-picker {
        --rgb-accent: ${({ theme }) => hexToRgbTriple(theme.material.colors.primary.main)};
        --rgb-background: ${({ theme }) => hexToRgbTriple(theme.material.colors.background.paper)};
        --rgb-input: ${({ theme }) => hexToRgbTriple(theme.material.colors.background.default)};
    }
`;

const computeOrigins = anchorEl => {
    if (!anchorEl || typeof anchorEl.getBoundingClientRect !== 'function') {
        return {
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
            transformOrigin: { vertical: 'top', horizontal: 'right' },
        };
    }
    const rect = anchorEl.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth || 0;
    const vh = window.innerHeight || document.documentElement.clientHeight || 0;
    const spaceRight = vw - rect.right;
    const spaceBelow = vh - rect.bottom;
    const horizontal = spaceRight + rect.width < PICKER_WIDTH + VIEWPORT_MARGIN ? 'right' : 'left';
    const vertical = spaceBelow < PICKER_HEIGHT + VIEWPORT_MARGIN ? 'bottom' : 'top';
    return {
        anchorOrigin: {
            vertical: vertical === 'top' ? 'bottom' : 'top',
            horizontal: horizontal === 'left' ? 'left' : 'right',
        },
        transformOrigin: { vertical, horizontal },
    };
};

const MessageReactionPicker = ({ anchorEl, open, onClose, onSelect, theme, customEmojis }) => {
    const customCategories = useMemo(() => buildCustomCategory(customEmojis), [customEmojis]);
    const [rcDataReady, setRcDataReady] = useState(Boolean(getRcEmojiData()));
    // Lazy-load ~135 KB RC emoji dataset on first picker open. Kept out of main bundle.
    useEffect(() => {
        if (!open || rcDataReady) return undefined;
        let cancelled = false;
        ensureRcEmojiData()
            .then(() => { if (!cancelled) setRcDataReady(true); })
            .catch(err => {
                // eslint-disable-next-line no-console
                console.warn('[reactions] failed to load emoji dataset', err);
            });
        return () => { cancelled = true; };
    }, [open, rcDataReady]);
    // emoji-mart mutates data.categories[].emojis on init. Build fresh each open and key the
    // Picker so React remounts on every open — guarantees emoji-mart re-processes the dataset.
    const pickerKey = useMemo(() => (open && rcDataReady ? `picker-${Date.now()}` : 'picker-closed'), [open, rcDataReady]);
    const rcSafeData = useMemo(() => (open && rcDataReady ? buildRcSafeEmojiData() : null), [pickerKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const { anchorOrigin, transformOrigin } = open
        ? computeOrigins(anchorEl)
        : { anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } };

    useEffect(() => {
        if (!open) return undefined;
        const handler = e => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    // emoji-mart renders nav → search → scroll inside a shadow DOM. Inject CSS to flip
    // nav and search via flex order so search sits on top and category tabs follow it.
    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        let timeoutId = null;
        const inject = (attempt = 0) => {
            if (cancelled) return;
            const host = document.querySelector('em-emoji-picker');
            const root = host && host.shadowRoot;
            if (!root) {
                if (attempt < 30) {
                    timeoutId = setTimeout(() => inject(attempt + 1), 50);
                }
                return;
            }
            if (root.querySelector('style[data-reorder="rc"]')) return;
            const style = document.createElement('style');
            style.setAttribute('data-reorder', 'rc');
            style.textContent = `
                section { display: flex; flex-direction: column; }
                #search { order: 1; padding-top: 8px; }
                #nav { order: 2; }
                .scroll { order: 3; }
            `;
            root.appendChild(style);
        };
        inject();
        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [open, pickerKey]);

    const handleSelect = emoji => {
        if (!emoji || !emoji.id) return;
        onSelect(`:${emoji.id}:`);
    };
    const isLight =
        typeof theme?.isLightColor === 'function' ? theme.isLightColor(theme?.material?.colors?.background?.default) : false;
    const pickerMode = isLight ? 'light' : 'dark';

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={anchorOrigin}
            transformOrigin={transformOrigin}
            marginThreshold={VIEWPORT_MARGIN}
            getContentAnchorEl={null}
            PaperProps={POPOVER_PAPER_PROPS}
        >
            <PickerFrame>
                <Suspense fallback={<span>Loading emoji picker...</span>}>
                    {open && rcSafeData && (
                        <Picker
                            key={pickerKey}
                            data={rcSafeData}
                            custom={customCategories}
                            onEmojiSelect={handleSelect}
                            theme={pickerMode}
                            previewPosition="none"
                            skinTonePosition="none"
                            navPosition="top"
                            perLine={8}
                            emojiButtonSize={32}
                            emojiSize={20}
                        />
                    )}
                    {open && !rcDataReady && <span>Loading emoji picker...</span>}
                </Suspense>
            </PickerFrame>
        </Popover>
    );
};

MessageReactionPicker.propTypes = {
    anchorEl: PropTypes.any,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    theme: PropTypes.object,
    customEmojis: PropTypes.array,
};

export default withTheme(MessageReactionPicker);
